/**
 * Phase 21.4 Batch 3E — Winner Celebration surface.
 *
 * Route: /contests/:slug/results/:entryId
 * Auth-required. Payload comes from the SERVER — coin_rewards ledger,
 * promo_plays audit, hall_of_champions row, and badge_catalog. Never
 * shows optimistic frontend state.
 *
 * Includes:
 *  - Placement banner (🥇/🥈/🥉)
 *  - Exact rewards granted (coins, promo plays, badges)
 *  - Link to Hall of Champions
 *  - Winner Share Card generator (Story + Square + Copy link)
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SharedNavigation from "@/components/SharedNavigation";
import WinnerShareCard from "@/components/contests/WinnerShareCard";
import api, { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { resolveShareCover, optimizeImage } from "@/lib/mediaUrls";
import { useShareVisibility } from "@/lib/useShareVisibility";

const positionEmoji = (p) => (p === 1 ? "🥇" : p === 2 ? "🥈" : p === 3 ? "🥉" : "🏆");
const positionLabel = (p) => (p === 1 ? "1st Place" : p === 2 ? "2nd Place" : p === 3 ? "3rd Place" : "Placement");

const ContestResultPage = () => {
  const { slug, entryId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  // Feb 2026 — CEO share-visibility gate for the winner share flow.
  const { canShare } = useShareVisibility();
  const winnersShareOn = canShare("winners");

  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("session_token");

  useEffect(() => {
    if (!isAuthed) {
      navigate(`/login?next=${encodeURIComponent(`/contests/${slug}/results/${entryId}`)}`);
    }
  }, [isAuthed, navigate, slug, entryId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/contests/${slug}/results/${entryId}`);
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "not_your_entry") toast.error("This result belongs to another artist.");
      else if (detail === "not_a_winner") toast.error("This entry is not a winner.");
      else toast.error("Failed to load result");
      navigate(`/contests/${slug}`);
    } finally {
      setLoading(false);
    }
  }, [slug, entryId, navigate]);

  useEffect(() => {
    if (isAuthed) load();
  }, [isAuthed, load]);

  useEffect(() => {
    if (!isAuthed) return;
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, [isAuthed]);

  if (!isAuthed) return null;

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <p className="pt-24 px-4 text-gray-500" data-testid="contest-result-loading">Loading your win…</p>
      </div>
    );
  }

  const pos = data.position;
  const emoji = positionEmoji(pos);
  const label = positionLabel(pos);
  const contest = data.contest || {};
  const entry = data.entry || {};
  const granted = data.granted || {};

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="contest-result-page">
      <SharedNavigation />

      {/* Confetti-like gradient hero */}
      <div className="relative pt-20 pb-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/40 via-purple-900/30 to-black" />
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, rgba(255,215,0,0.35), transparent 50%), radial-gradient(circle at 80% 60%, rgba(168,85,247,0.35), transparent 50%)",
        }} />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🏆 RGM Contest Center</p>
          <p className="text-6xl sm:text-8xl mb-3 mt-2 leading-none" data-testid="contest-result-emoji">{emoji}</p>
          <h1 className="text-3xl sm:text-5xl font-black text-white leading-tight" data-testid="contest-result-headline">
            You placed {label}!
          </h1>
          <p className="text-sm sm:text-base text-gray-300 mt-2" data-testid="contest-result-contest-title">
            {contest.title}
          </p>
          <div className="mt-6 flex flex-wrap gap-2 justify-center">
              {data.winner_cover_url && (
                <>
                  <a
                    href={data.winner_cover_url}
                    download={`rgm-winner-${contest.slug || "contest"}-p${pos}.png`}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-black text-sm font-black transition-colors"
                    data-testid="contest-result-download-cover"
                    target="_blank"
                    rel="noreferrer"
                  >
                    ⬇ Download Cover
                  </a>
                  <img
                    src={optimizeImage(data.winner_cover_url, 'card')}
                    alt={`${label} winner cover`}
                    className="hidden"
                    data-testid="contest-result-cover-img"
                  />
                </>
              )}
            {winnersShareOn && (
              <Button
                onClick={() => setShareOpen(true)}
                className="bg-white text-black hover:bg-amber-200 font-black"
                data-testid="contest-result-share-btn"
              >
                📸 Share your win
              </Button>
            )}
            <Link
              to="/hall-of-champions"
              className="inline-flex items-center px-4 py-2 rounded-md bg-white/10 border border-white/15 text-white text-sm font-black hover:bg-white/20 transition-colors"
              data-testid="contest-result-hof-btn"
            >
              🏛 Hall of Champions →
            </Link>
            <Link
              to={`/contests/${slug}`}
              className="inline-flex items-center px-4 py-2 rounded-md bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 transition-colors"
              data-testid="contest-result-contest-btn"
            >
              ← Contest
            </Link>
          </div>

          {/* iter_184 — Preview the auto-generated 1500×1500 cover so
              winners see exactly what they're downloading / sharing. */}
          {data.winner_cover_url && (
            <div className="mt-6 flex justify-center">
              <img
                src={optimizeImage(data.winner_cover_url, 'card')}
                alt={`${label} winner cover`}
                className="w-full max-w-md rounded-2xl border border-amber-500/40 shadow-2xl"
                data-testid="contest-result-cover-preview"
                loading="lazy"
              />
            </div>
          )}
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 pb-16 space-y-6">
        {/* Rewards actually granted */}
        <section className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-900/25 to-black p-5 sm:p-6" data-testid="contest-result-rewards">
          <h2 className="text-lg font-black mb-3">🏅 Rewards Granted</h2>
          <p className="text-[11px] text-gray-400 mb-3">
            These values come directly from the reward ledger — the exact amounts credited to your account.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-white/10 bg-black/50 p-3" data-testid="contest-result-coins">
              <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">Reward coins</p>
              <p className="text-2xl font-black text-white mt-0.5">🪙 {Number(granted.coins || 0).toLocaleString()}</p>
              <p className="text-[10px] text-gray-500 mt-1">Landed in your reward balance (spendable on RGM features, not withdrawable to cash).</p>
            </div>
            {contest.entry_type === "track" ? (
              <div className="rounded-lg border border-white/10 bg-black/50 p-3" data-testid="contest-result-promo">
                <p className="text-[10px] uppercase tracking-widest text-purple-300 font-black">Promo plays</p>
                <p className="text-2xl font-black text-white mt-0.5">⚡ {Number(granted.promo_plays || 0).toLocaleString()}</p>
                <p className="text-[10px] text-gray-500 mt-1">Added to your track&apos;s display play-count.</p>
              </div>
            ) : (
              <div className="rounded-lg border border-white/10 bg-black/30 p-3 opacity-60" data-testid="contest-result-promo-na">
                <p className="text-[10px] uppercase tracking-widest text-gray-500 font-black">Promo plays</p>
                <p className="text-sm text-gray-400 mt-0.5">Not applicable for beat contests</p>
              </div>
            )}
          </div>

          <div className="mt-4" data-testid="contest-result-badges">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black mb-2">Badges awarded</p>
            {(granted.badges || []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {granted.badges.map((b) => (
                  <div
                    key={b.badge_id}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/50 bg-amber-500/15 text-amber-100"
                    data-testid={`contest-result-badge-${b.badge_id}`}
                  >
                    {b.icon_url && (
                      <img src={b.icon_url} alt={b.label} className="w-5 h-5 object-contain" />
                    )}
                    <span className="text-xs font-black">{b.label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500" data-testid="contest-result-badges-empty">No badges awarded for this contest.</p>
            )}
          </div>
        </section>

        {/* Entry snapshot */}
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6" data-testid="contest-result-entry">
          <h2 className="text-lg font-black mb-3">Your winning entry</h2>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
              {(() => {
                // iter_188 — Canonical share-cover resolver. Prefers the
                // LIVE track cover (server-populated `track_cover_url` on
                // the entry payload) over the snapshot which may hold a
                // stale / bare filename.
                const src = resolveShareCover({
                  cover_url: entry.track_cover_url,
                  cover_art: entry.ref_snapshot?.cover_art,
                });
                return src ? (
                  <img
                    src={src}
                    alt={entry.ref_snapshot?.title || ""}
                    className="w-full h-full object-cover"
                    data-testid="contest-result-entry-cover"
                    onError={(e) => {
                      // Hide broken images so the fallback icon shows.
                      e.currentTarget.style.display = "none";
                      const parent = e.currentTarget.parentElement;
                      if (parent) parent.dataset.brokenImage = "1";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl">
                    {entry.entry_type === "beat" ? "🎧" : "🎤"}
                  </div>
                );
              })()}
            </div>
            <div className="min-w-0">
              <p className="text-white font-black text-base truncate">{entry.ref_snapshot?.title || entry.ref_id}</p>
              <p className="text-xs text-gray-400">{entry.entry_type === "beat" ? "Beat entry" : "Track entry"}</p>
            </div>
          </div>
        </section>
      </div>

      {/* Share modal */}
      {shareOpen && winnersShareOn && (
        <WinnerShareCard
          data={data}
          artist={me}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
};

export default ContestResultPage;
